import React from 'react';
import { usePermissions, PermissionCode } from '../../contexts/PermissionsContext';

interface PermissionGuardProps {
  permission?: PermissionCode;
  permissions?: PermissionCode[];
  requireAll?: boolean; // If true, requires ALL permissions; if false (default), requires ANY
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component that conditionally renders children based on user permissions
 * 
 * Usage:
 * <PermissionGuard permission="demands.create">
 *   <button>Create Demand</button>
 * </PermissionGuard>
 * 
 * <PermissionGuard permissions={["demands.edit", "demands.assign"]} requireAll={false}>
 *   <button>Edit or Assign</button>
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  // While loading permissions, don't show anything
  if (loading) {
    return <>{fallback}</>;
  }

  // Single permission check
  if (permission) {
    return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
  }

  // Multiple permissions check
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
    
    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  // No permission specified, render children by default
  return <>{children}</>;
};

interface AdminGuardProps {
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Component that only renders for admin users
 */
export const AdminGuard: React.FC<AdminGuardProps> = ({ fallback = null, children }) => {
  const { isAdmin, loading } = usePermissions();

  if (loading) {
    return <>{fallback}</>;
  }

  return isAdmin ? <>{children}</> : <>{fallback}</>;
};

/**
 * Component that only renders for super admin users
 */
export const SuperAdminGuard: React.FC<AdminGuardProps> = ({ fallback = null, children }) => {
  const { isSuperAdmin, loading } = usePermissions();

  if (loading) {
    return <>{fallback}</>;
  }

  return isSuperAdmin ? <>{children}</> : <>{fallback}</>;
};
